import { useEffect, useMemo, useState } from 'react'
import { Alert, Modal, notification, Radio } from 'antd'
import { RadioChangeEvent } from 'antd/lib'

import { Queries } from 'admin/lib/Api'
import { CompanyEditForm } from './CompanyEditForm_v2'

type CompanyModalProps = {
  open: boolean
  onClose: () => void
  company?: {
    id: string
    isManufacturer: boolean
  }
  hasMatch?: boolean
}

export function CompanyModal({
  open,
  onClose,
  company,
  hasMatch
}: CompanyModalProps) {
  const [type, setType] = useState<'supplier' | 'manufacturer' | undefined>(
    undefined
  )

  const isNew: boolean = useMemo(() => !company, [company])

  useEffect(() => {
    if (company && company.id) {
      setType(company.isManufacturer ? 'manufacturer' : 'supplier')
    }
  }, [company])

  useEffect(() => {
    return () => {
      setType(undefined)
      notification.destroy()
    }
  }, [])

  const companyUpdate = Queries.useSupplierManufacturerUpsert(isNew, type)

  const onChange = (e: RadioChangeEvent): void => {
    setType(e.target.value)
  }

  const handleCancel = () => {
    onClose()
    setType(undefined)
  }

  const handleSave = company => {
    notification.destroy()

    companyUpdate.mutate(company, {
      onSuccess: () => {
        notification.success({
          message: isNew
            ? `Successfully created ${type}`
            : `Successfully updated ${type}`
        })
        handleCancel()
      },
      onError: (error: unknown) => {
        const err = error as { response: { data: unknown } }
        notification.error({
          message: 'Error 😞',
          description:
            typeof err.response.data === 'string'
              ? err.response.data
              : JSON.stringify(err.response.data),
          duration: 0
        })
      }
    })
  }

  return (
    <Modal
      title={isNew ? 'Add new company' : `Edit ${type}`}
      open={open}
      closable
      onCancel={onClose}
      destroyOnHidden={true}
      footer={null}
    >
      {isNew && (
        <>
          <div>Company Type</div>
          <div>
            <Radio.Group
              value={type}
              optionType="button"
              options={[
                { value: 'supplier', label: 'Supplier' },
                { value: 'manufacturer', label: 'Manufacturer' }
              ]}
              onChange={onChange}
            />
          </div>
        </>
      )}
      {hasMatch && (
        <Alert
          message={`This ${type} has a matching ${type === 'manufacturer' ? 'supplier' : 'manufacturer'}`}
          description="If you change the company name and/or number, both entries will be updated."
          type="warning"
          showIcon
        />
      )}
      <CompanyEditForm
        onSave={handleSave}
        onCancel={handleCancel}
        type={type}
        company={company}
      />
    </Modal>
  )
}

type CompanyModalWrapperProps = {
  open: boolean
  onClose: () => void
  company: {
    id: string
    isManufacturer: boolean
    hasMatch?: boolean
  }
}

export function ManufacturerCompanyModalWrapper({
  open,
  onClose,
  company
}: CompanyModalWrapperProps) {
  const existingCompany = Queries.useManufacturer(company.id)

  if (existingCompany.isLoading) {
    return null
  }
  if (existingCompany.isError) {
    return null
  }
  return (
    <CompanyModal
      open={open}
      onClose={onClose}
      company={{ isManufacturer: true, ...existingCompany.data }}
      hasMatch={company.hasMatch}
    />
  )
}

export function SupplierCompanyModalWrapper({
  open,
  onClose,
  company
}: CompanyModalWrapperProps) {
  const existingCompany = Queries.useSupplier(company.id)

  if (existingCompany.isLoading) {
    return null
  }
  if (existingCompany.isError) {
    return null
  }
  return (
    <CompanyModal
      open={open}
      onClose={onClose}
      company={{ isManufacturer: false, ...existingCompany.data }}
      hasMatch={company.hasMatch}
    />
  )
}
